import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Typography,
  Divider,
  List,
  Badge,
  Progress,
  Alert,
  Tabs,
} from 'antd';
import {
  AppstoreOutlined,
  ImportOutlined,
  ExportOutlined,
  BarChartOutlined,
  WarningOutlined,
  TrophyOutlined,
  DashboardOutlined,
  RightOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { stockService, reportService, invoiceService } from '../../services';
import { formatNumber, formatCurrency } from '../../utils/helpers';
import StockList from './StockList';
import ImportInvoices from './ImportInvoices';
import ExportInvoices from './ExportInvoices';
import StockReports from './StockReports';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const StockDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    totalProducts: 0,
    totalVariants: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
    totalInventoryValue: 0,
    pendingOrders: 0,
    todayRevenue: 0,
    bestSellingProducts: [],
    lowStockVariants: [],
    outOfStockVariants: [],
    recentImports: [],
    recentExports: [],
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load variants for stock analysis using new API
      const variantsResponse = await stockService.getVariants();
      const variants = variantsResponse.success ? variantsResponse.data || [] : [];

      // Analyze stock levels from variants
      const lowStockVariants = variants.filter(variant => {
        const stock = variant.stock || 0;
        const lowStockAlert = variant.low_stock_alert || 5;
        return stock <= lowStockAlert && stock > 0;
      });

      const outOfStockVariants = variants.filter(variant => (variant.stock || 0) === 0);

      // Calculate total inventory value
      const totalInventoryValue = variants.reduce((total, variant) => {
        const stock = variant.stock || 0;
        const price = parseFloat(variant.price || 0);
        return total + (stock * price);
      }, 0);

      // Group variants by product to get unique products count
      const uniqueProducts = [...new Map(
        variants.map(variant => [variant.product_name, variant])
      ).values()];

      // Load recent data
      const [bestSellingRes, recentImportsRes, recentExportsRes] = await Promise.all([
        reportService.getBestSellingProducts({ limit: 5 }).catch(() => ({ success: false })),
        invoiceService.import.getAll({ limit: 5 }).catch(() => ({ success: false })),
        invoiceService.export.getAll({ limit: 5 }).catch(() => ({ success: false })),
      ]);

      setDashboardData({
        totalProducts: uniqueProducts.length,
        totalVariants: variants.length,
        lowStockCount: lowStockVariants.length,
        outOfStockCount: outOfStockVariants.length,
        totalInventoryValue: totalInventoryValue,
        pendingOrders: 0, // Will be calculated from exports
        todayRevenue: 0, // Will be calculated
        bestSellingProducts: bestSellingRes.success ? bestSellingRes.data?.slice(0, 5) || [] : [],
        lowStockVariants: lowStockVariants.slice(0, 5),
        outOfStockVariants: outOfStockVariants.slice(0, 3),
        recentImports: recentImportsRes.success ? recentImportsRes.data?.slice(0, 5) || [] : [],
        recentExports: recentExportsRes.success ? recentExportsRes.data?.slice(0, 5) || [] : [],
      });
    } catch (error) {
      //console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: 'Quản lý tồn kho',
      description: 'Xem và điều chỉnh tồn kho sản phẩm',
      icon: <AppstoreOutlined style={{ fontSize: '24px', color: '#1890ff' }} />,
      action: () => navigate('/stock/list'),
      color: '#e6f7ff',
      borderColor: '#1890ff',
    },
    {
      title: 'Phiếu nhập kho',
      description: 'Quản lý phiếu nhập hàng từ nhà cung cấp',
      icon: <ImportOutlined style={{ fontSize: '24px', color: '#52c41a' }} />,
      action: () => navigate('/stock/import'),
      color: '#f6ffed',
      borderColor: '#52c41a',
    },
    {
      title: 'Phiếu xuất kho',
      description: 'Theo dõi đơn hàng và xuất kho',
      icon: <ExportOutlined style={{ fontSize: '24px', color: '#fa541c' }} />,
      action: () => navigate('/stock/export'),
      color: '#fff2e8',
      borderColor: '#fa541c',
    },
    {
      title: 'Báo cáo kho',
      description: 'Phân tích dữ liệu và báo cáo',
      icon: <BarChartOutlined style={{ fontSize: '24px', color: '#722ed1' }} />,
      action: () => navigate('/stock/reports'),
      color: '#f9f0ff',
      borderColor: '#722ed1',
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
          <DashboardOutlined style={{ marginRight: 12 }} />
          Quản lý kho
        </Title>
        <Text type="secondary">Tổng quan và quản lý toàn bộ hoạt động kho</Text>
      </div>

      {/* Thống kê tổng quan */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Tổng sản phẩm"
              value={dashboardData.totalProducts}
              prefix={<AppstoreOutlined />}
              formatter={(value) => formatNumber(value)}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {formatNumber(dashboardData.totalVariants)} variants
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Giá trị tồn kho"
              value={dashboardData.totalInventoryValue}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Sắp hết hàng"
              value={dashboardData.lowStockCount}
              prefix={<WarningOutlined />}
              valueStyle={{ color: dashboardData.lowStockCount > 0 ? '#fa8c16' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic
              title="Hết hàng"
              value={dashboardData.outOfStockCount}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: dashboardData.outOfStockCount > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Cảnh báo */}
      {(dashboardData.lowStockCount > 0 || dashboardData.outOfStockCount > 0) && (
        <Alert
          message="Cảnh báo tồn kho"
          description={
            `Có ${dashboardData.outOfStockCount} Sản phẩm hết hàng và ${dashboardData.lowStockCount} sản phẩm sắp hết hàng. 
             Vui lòng kiểm tra và bổ sung kho.`
          }
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button size="small" onClick={() => navigate('/stock/list')}>
              Xem chi tiết
            </Button>
          }
        />
      )}

      {/* Chức năng chính */}
      <Card title="Chức năng chính" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          {quickActions.map((action, index) => (
            <Col xs={24} sm={12} md={6} key={index}>
              <Card
                hoverable
                onClick={action.action}
                style={{
                  borderColor: action.borderColor,
                  background: action.color,
                  cursor: 'pointer',
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div style={{ textAlign: 'center' }}>
                  {action.icon}
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: 8 }}>
                      {action.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {action.description}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Thông tin nhanh */}
      <Row gutter={[16, 16]}>
        {/* Sản phẩm bán chạy */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <TrophyOutlined />
                <span>Sản phẩm bán chạy</span>
              </Space>
            }
            extra={
              <Button 
                type="link" 
                icon={<RightOutlined />}
                onClick={() => navigate('/stock/reports')}
              >
                Xem tất cả
              </Button>
            }
          >
            <List
              size="small"
              dataSource={dashboardData.bestSellingProducts}
              locale={{ emptyText: 'Chưa có dữ liệu' }}
              renderItem={(item, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={
                      <Badge
                        count={index + 1}
                        style={{
                          backgroundColor: index < 3 ? '#fa8c16' : '#d9d9d9',
                          color: index < 3 ? '#fff' : '#666'
                        }}
                      />
                    }
                    title={item.product_name}
                    description={
                      <Space size="large">
                        <span>Đã bán: <strong>{formatNumber(item.total_sold || 0)}</strong></span>
                        <span>Doanh thu: <strong>{formatCurrency(item.total_revenue || 0)}</strong></span>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Sản phẩm tồn kho thấp */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <WarningOutlined style={{ color: '#fa8c16' }} />
                <span>Sản phẩm sắp hết hàng</span>
              </Space>
            }
            extra={
              <Button 
                type="link" 
                icon={<RightOutlined />}
                onClick={() => navigate('/stock/list')}
              >
                Xem tất cả
              </Button>
            }
          >
            <List
              size="small"
              dataSource={dashboardData.lowStockVariants}
              locale={{ emptyText: 'Không có sản phẩm nào sắp hết hàng' }}
              renderItem={(variant) => {
                const stock = variant.stock || 0;
                const lowStockAlert = variant.low_stock_alert || 5;
                const maxStock = lowStockAlert * 4; // Assume max stock = 4x low stock alert
                const percentage = maxStock > 0 ? (stock / maxStock) * 100 : 0;
                
                return (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <span>{variant.product_name}</span>
                          <Badge 
                            count={stock} 
                            style={{ 
                              backgroundColor: stock === 0 ? '#ff4d4f' : '#fa8c16' 
                            }} 
                          />
                        </Space>
                      }
                      description={
                        <div>
                          <div style={{ marginBottom: 4, fontSize: '12px' }}>
                            <Text type="secondary">SKU: {variant.sku}</Text>
                            {variant.attributes && (
                              <Text type="secondary" style={{ marginLeft: 8 }}>
                                | {variant.attributes}
                              </Text>
                            )}
                          </div>
                          <div style={{ marginBottom: 4 }}>
                            Tồn kho: {formatNumber(stock)} / Cảnh báo: {formatNumber(lowStockAlert)}
                          </div>
                          <Progress 
                            percent={Math.min(percentage, 100)} 
                            size="small" 
                            status={stock === 0 ? 'exception' : percentage < 20 ? 'active' : 'normal'}
                            showInfo={false}
                          />
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
            
            {/* Show out of stock variants if any */}
            {dashboardData.outOfStockVariants.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ marginBottom: 8 }}>
                  <Text strong style={{ color: '#ff4d4f' }}>
                    <ExclamationCircleOutlined /> Sản phẩm hết hàng:
                  </Text>
                </div>
                <List
                  size="small"
                  dataSource={dashboardData.outOfStockVariants}
                  renderItem={(variant) => (
                    <List.Item style={{ padding: '4px 0' }}>
                      <List.Item.Meta
                        title={
                          <Space>
                            <span style={{ color: '#ff4d4f' }}>{variant.product_name}</span>
                            <Badge count={0} style={{ backgroundColor: '#ff4d4f' }} />
                          </Space>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            SKU: {variant.sku} {variant.attributes && `| ${variant.attributes}`}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Divider style={{ margin: '32px 0' }} />

      {/* Tab management sections */}
      <Tabs defaultActiveKey="overview" size="large">
        <TabPane tab="Tổng quan" key="overview">
          {/* Already displayed above */}
        </TabPane>
        <TabPane tab="Quản lý tồn kho" key="stock">
          <StockList />
        </TabPane>
        <TabPane tab="Phiếu nhập" key="import">
          <ImportInvoices />
        </TabPane>
        <TabPane tab="Phiếu xuất" key="export">
          <ExportInvoices />
        </TabPane>
        <TabPane tab="Báo cáo" key="reports">
          <StockReports />
        </TabPane>
      </Tabs>
    </div>
  );
};

export default StockDashboard;
